import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { usePushNotifications } from '../contexts/PushNotificationContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  AlertTriangle, Camera, CheckCircle, Clock, Home, List, MapPin,
  Plus, RefreshCw, Send, User, Wrench, Shield, Sparkles, Navigation,
  Phone, QrCode, Play, Pause, Check, X, ChevronRight, Loader2, Bell, BellOff,
  Scan, Image, Upload, Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  critical: "bg-red-500 text-white",
  urgent: "bg-red-500/20 text-red-400",
  high: "bg-orange-500/20 text-orange-400",
  medium: "bg-yellow-500/20 text-yellow-400",
  low: "bg-blue-500/20 text-blue-400",
};

const STATUS_COLORS = {
  open: "bg-red-500",
  acknowledged: "bg-blue-500",
  assigned: "bg-purple-500",
  in_progress: "bg-yellow-500",
  started: "bg-yellow-500",
  accepted: "bg-blue-500",
  completed: "bg-green-500",
  resolved: "bg-green-500",
  closed: "bg-gray-500",
};

export default function MobileStaffPage() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { isSupported, isSubscribed, subscribe, unsubscribe, loading: pushLoading, sendTestNotification } = usePushNotifications();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("tasks");
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showQRScanDialog, setShowQRScanDialog] = useState(false);
  const [scannedLocation, setScannedLocation] = useState(null);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Data states
  const [myTasks, setMyTasks] = useState([]);
  const [malls, setMalls] = useState([]);
  const [selectedMall, setSelectedMall] = useState(null);
  const [allIssues, setAllIssues] = useState([]);
  
  // Dialog states
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // Form states
  const [reportForm, setReportForm] = useState({
    title: "", description: "", category: "maintenance", priority: "medium",
    location_zone: "", floor: 0, photo_urls: []
  });
  const [updateForm, setUpdateForm] = useState({
    status: "started", notes: "", photo_urls: []
  });
  const [qrCodeInput, setQrCodeInput] = useState("");
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          setLocationError("Location access denied. Some features may be limited.");
          console.warn("Geolocation error:", error);
        },
        { enableHighAccuracy: true }
      );
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [tasksRes, mallsRes] = await Promise.all([
        axios.get(`${API}/api/tenant-service/mobile/my-tasks`, { headers }),
        axios.get(`${API}/api/malls`, { headers })
      ]);
      
      setMyTasks(tasksRes.data);
      setMalls(mallsRes.data);
      
      if (mallsRes.data.length > 0) {
        setSelectedMall(mallsRes.data[0]);
        // Fetch issues for first mall
        const issuesRes = await axios.get(`${API}/api/tenant-service/mobile/issues/${mallsRes.data[0].id}`, { headers });
        setAllIssues(issuesRes.data);
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchIssuesForMall = async (mallId) => {
    try {
      const res = await axios.get(`${API}/api/tenant-service/mobile/issues/${mallId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllIssues(res.data);
    } catch (err) {
      console.error("Failed to fetch issues:", err);
    }
  };

  const handleMallChange = (mallId) => {
    const mall = malls.find(m => m.id === mallId);
    setSelectedMall(mall);
    fetchIssuesForMall(mallId);
  };

  // QR Code Scanning
  const handleScanQR = async () => {
    if (!qrCodeInput.trim()) {
      toast.error("Please enter a QR code");
      return;
    }
    
    try {
      const res = await axios.post(`${API}/api/qr/scan`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { qr_code: qrCodeInput.trim() }
      });
      
      const loc = res.data.location;
      setScannedLocation(loc);
      
      // Auto-fill form with location data
      setReportForm(prev => ({
        ...prev,
        location_zone: loc.zone || loc.location_name,
        floor: loc.floor
      }));
      
      toast.success(`Location: ${loc.location_name} (Floor ${loc.floor})`);
      setShowQRScanDialog(false);
      setQrCodeInput("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "QR code not recognized");
    }
  };

  // Photo Upload
  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const newPhotos = [];
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mall_id', selectedMall?.id || 'general');
        formData.append('category', 'issue');
        
        const res = await axios.post(`${API}/api/qr/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        newPhotos.push(res.data.file.url);
      } catch (err) {
        console.error("Upload failed:", err);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    if (newPhotos.length > 0) {
      setUploadedPhotos(prev => [...prev, ...newPhotos]);
      setReportForm(prev => ({
        ...prev,
        photo_urls: [...prev.photo_urls, ...newPhotos]
      }));
      toast.success(`${newPhotos.length} photo(s) uploaded`);
    }
    
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
    setReportForm(prev => ({
      ...prev,
      photo_urls: prev.photo_urls.filter((_, i) => i !== index)
    }));
  };

  const handleReportIssue = async () => {
    if (!reportForm.title || !reportForm.description || !selectedMall) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...reportForm,
        mall_id: selectedMall.id,
        latitude: location?.latitude || null,
        longitude: location?.longitude || null,
        // Include QR scanned location data if available
        qr_location_id: scannedLocation?.id || null,
        qr_code: scannedLocation?.qr_code || null,
      };
      
      await axios.post(`${API}/api/tenant-service/mobile/report`, payload, 
        { headers: { Authorization: `Bearer ${token}` }});
      
      toast.success("Issue reported successfully");
      setShowReportDialog(false);
      setReportForm({ title: "", description: "", category: "maintenance", priority: "medium", location_zone: "", floor: 0, photo_urls: [] });
      setUploadedPhotos([]);
      setScannedLocation(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to report issue");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateTask = async () => {
    if (!selectedTask) return;
    
    setSubmitting(true);
    try {
      await axios.put(
        `${API}/api/tenant-service/mobile/task/${selectedTask.task_type}/${selectedTask.id}/status`,
        {
          ...updateForm,
          latitude: location?.latitude,
          longitude: location?.longitude
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success("Task updated successfully");
      setShowUpdateDialog(false);
      setSelectedTask(null);
      setUpdateForm({ status: "started", notes: "", photo_urls: [] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update task");
    } finally {
      setSubmitting(false);
    }
  };

  const openUpdateDialog = (task, initialStatus) => {
    setSelectedTask(task);
    setUpdateForm({ ...updateForm, status: initialStatus });
    setShowUpdateDialog(true);
  };

  const getTaskIcon = (type) => {
    switch (type) {
      case 'maintenance': return <Wrench className="w-5 h-5" />;
      case 'security': return <Shield className="w-5 h-5" />;
      case 'housekeeping': return <Sparkles className="w-5 h-5" />;
      default: return <AlertTriangle className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-primary animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="mobile-staff-page">
      {/* Header */}
      <div className="bg-card border-b sticky top-0 z-10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-sm">{user?.name || 'Staff'}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Push Notification Toggle */}
            <Button 
              size="icon" 
              variant={isSubscribed ? "default" : "ghost"} 
              onClick={() => setShowSettingsDialog(true)}
              data-testid="notification-toggle"
            >
              {isSubscribed ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
            </Button>
            {location ? (
              <Badge variant="outline" className="text-green-400 border-green-400">
                <Navigation className="w-3 h-3 mr-1" />
                GPS
              </Badge>
            ) : (
              <Badge variant="outline" className="text-red-400 border-red-400">
                <Navigation className="w-3 h-3 mr-1" />
                No GPS
              </Badge>
            )}
            <Button size="icon" variant="ghost" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Mall Selector */}
        <div className="mt-3">
          <Select value={selectedMall?.id} onValueChange={handleMallChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Mall" />
            </SelectTrigger>
            <SelectContent>
              {malls.map(mall => (
                <SelectItem key={mall.id} value={mall.id}>{mall.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Location Warning */}
      {locationError && (
        <div className="mx-4 mt-4 p-3 bg-yellow-500/20 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-500" />
          <p className="text-sm text-yellow-400">{locationError}</p>
        </div>
      )}

      {/* Quick Actions */}
      <div className="p-4 grid grid-cols-2 gap-3">
        <Button 
          className="h-20 flex flex-col items-center justify-center gap-2" 
          onClick={() => setShowReportDialog(true)}
          data-testid="report-issue-btn"
        >
          <AlertTriangle className="w-6 h-6" />
          <span>Report Issue</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-20 flex flex-col items-center justify-center gap-2"
          onClick={() => navigate('/operations')}
        >
          <Home className="w-6 h-6" />
          <span>Operations</span>
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="px-4">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="tasks" className="text-sm">
            <List className="w-4 h-4 mr-1" />
            My Tasks
          </TabsTrigger>
          <TabsTrigger value="issues" className="text-sm">
            <AlertTriangle className="w-4 h-4 mr-1" />
            All Issues
          </TabsTrigger>
          <TabsTrigger value="history" className="text-sm">
            <Clock className="w-4 h-4 mr-1" />
            History
          </TabsTrigger>
        </TabsList>

        {/* My Tasks Tab */}
        <TabsContent value="tasks" className="mt-4 space-y-3">
          {myTasks.filter(t => !['completed', 'closed', 'resolved'].includes(t.status)).length > 0 ? (
            myTasks.filter(t => !['completed', 'closed', 'resolved'].includes(t.status)).map(task => (
              <Card key={task.id} className="overflow-hidden" data-testid={`task-card-${task.id}`}>
                <div className={`h-1 ${STATUS_COLORS[task.status]}`} />
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${task.priority === 'urgent' || task.priority === 'critical' ? 'bg-red-500/20 text-red-500' : 'bg-muted text-muted-foreground'}`}>
                      {getTaskIcon(task.task_type || task.category)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-sm truncate">{task.title}</p>
                        <Badge className={PRIORITY_COLORS[task.priority]} variant="outline">{task.priority}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {task.ticket_number || task.job_number || task.id.slice(0, 8)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {task.location_zone && `${task.location_zone} • `}Floor {task.floor || 0}
                      </p>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2 mt-3">
                        {task.status === 'assigned' || task.status === 'open' ? (
                          <Button size="sm" onClick={() => openUpdateDialog(task, 'accepted')}>
                            <Play className="w-3 h-3 mr-1" />
                            Accept
                          </Button>
                        ) : task.status === 'accepted' || task.status === 'in_progress' || task.status === 'started' ? (
                          <Button size="sm" onClick={() => openUpdateDialog(task, 'completed')}>
                            <Check className="w-3 h-3 mr-1" />
                            Complete
                          </Button>
                        ) : null}
                        <Button size="sm" variant="outline" onClick={() => openUpdateDialog(task, task.status)}>
                          Update
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="p-8 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500/50 mb-3" />
              <p className="text-muted-foreground">No pending tasks</p>
            </Card>
          )}
        </TabsContent>

        {/* All Issues Tab */}
        <TabsContent value="issues" className="mt-4 space-y-3">
          {allIssues.filter(i => i.status !== 'resolved' && i.status !== 'closed').length > 0 ? (
            allIssues.filter(i => i.status !== 'resolved' && i.status !== 'closed').map(issue => (
              <Card key={issue.id} data-testid={`issue-card-${issue.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${issue.priority === 'urgent' || issue.priority === 'critical' ? 'bg-red-500/20 text-red-500' : 'bg-muted text-muted-foreground'}`}>
                        {getTaskIcon(issue.category)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{issue.title}</p>
                        <p className="text-xs text-muted-foreground">{issue.ticket_number}</p>
                        <p className="text-xs text-muted-foreground mt-1">{issue.category} • {issue.location_zone || 'N/A'}</p>
                        {issue.reporter_name && (
                          <p className="text-xs text-muted-foreground">Reported by: {issue.reporter_name}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={PRIORITY_COLORS[issue.priority]}>{issue.priority}</Badge>
                      <p className="text-xs text-muted-foreground mt-1">{new Date(issue.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="p-8 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500/50 mb-3" />
              <p className="text-muted-foreground">No open issues</p>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4 space-y-3">
          {myTasks.filter(t => ['completed', 'closed', 'resolved'].includes(t.status)).length > 0 ? (
            myTasks.filter(t => ['completed', 'closed', 'resolved'].includes(t.status)).map(task => (
              <Card key={task.id} className="opacity-75">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium text-sm">{task.title}</p>
                        <p className="text-xs text-muted-foreground">{task.ticket_number || task.job_number}</p>
                      </div>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400">{task.status}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="p-8 text-center">
              <Clock className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">No completed tasks yet</p>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Report Issue Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-primary" />
              Report Issue
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* QR Scan Button */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={() => setShowQRScanDialog(true)}
                data-testid="scan-qr-btn"
              >
                <Scan className="w-4 h-4 mr-2" />
                Scan QR Location
              </Button>
            </div>
            
            {/* Scanned Location Info */}
            {scannedLocation && (
              <div className="p-3 bg-primary/10 rounded-lg flex items-center gap-2 border border-primary/20">
                <QrCode className="w-4 h-4 text-primary" />
                <div className="text-sm">
                  <p className="font-medium">{scannedLocation.location_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {scannedLocation.location_type} • Floor {scannedLocation.floor}
                  </p>
                </div>
              </div>
            )}
            
            <div>
              <Label>Category *</Label>
              <Select value={reportForm.category} onValueChange={v => setReportForm({...reportForm, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                  <SelectItem value="housekeeping">Housekeeping</SelectItem>
                  <SelectItem value="safety">Safety Hazard</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input 
                value={reportForm.title} 
                onChange={e => setReportForm({...reportForm, title: e.target.value})} 
                placeholder="Brief description"
                data-testid="issue-title-input"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea 
                value={reportForm.description} 
                onChange={e => setReportForm({...reportForm, description: e.target.value})} 
                placeholder="Detailed description..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priority</Label>
                <Select value={reportForm.priority} onValueChange={v => setReportForm({...reportForm, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Floor</Label>
                <Input 
                  type="number" 
                  value={reportForm.floor} 
                  onChange={e => setReportForm({...reportForm, floor: parseInt(e.target.value) || 0})} 
                />
              </div>
            </div>
            <div>
              <Label>Zone/Location</Label>
              <Input 
                value={reportForm.location_zone} 
                onChange={e => setReportForm({...reportForm, location_zone: e.target.value})} 
                placeholder="e.g., North Wing, Near Entrance"
              />
            </div>
            
            {/* GPS Location Info */}
            {location && (
              <div className="p-3 bg-green-500/10 rounded-lg flex items-center gap-2">
                <MapPin className="w-4 h-4 text-green-500" />
                <span className="text-xs text-green-400">
                  GPS: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                </span>
              </div>
            )}
            
            {/* Photo Upload */}
            <div>
              <Label>Photos</Label>
              <input
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                className="hidden"
              />
              
              {/* Uploaded Photos Preview */}
              {uploadedPhotos.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {uploadedPhotos.map((url, index) => (
                    <div key={index} className="relative w-16 h-16">
                      <img src={`${API}${url}`} alt={`Photo ${index + 1}`} className="w-16 h-16 object-cover rounded-lg border" />
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 mr-2" />
                )}
                {uploading ? 'Uploading...' : uploadedPhotos.length > 0 ? 'Add More Photos' : 'Take/Upload Photo'}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>Cancel</Button>
            <Button onClick={handleReportIssue} disabled={submitting} data-testid="submit-issue-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Scan Dialog */}
      <Dialog open={showQRScanDialog} onOpenChange={setShowQRScanDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scan className="w-5 h-5 text-primary" />
              Scan QR Location
            </DialogTitle>
            <DialogDescription>
              Enter the QR code from the location tag
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>QR Code</Label>
              <Input
                value={qrCodeInput}
                onChange={(e) => setQrCodeInput(e.target.value)}
                placeholder="Enter or scan QR code (e.g., FPT-MALL-ZON-0001)"
                data-testid="qr-code-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Format: FPT-MALLID-TYPE-NUMBER
              </p>
            </div>
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <QrCode className="w-12 h-12 mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">
                Point your camera at a QR code or enter it manually
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQRScanDialog(false)}>Cancel</Button>
            <Button onClick={handleScanQR} data-testid="confirm-scan-btn">
              <Check className="w-4 h-4 mr-2" />
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Task Dialog */}
      <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Status *</Label>
              <Select value={updateForm.status} onValueChange={v => setUpdateForm({...updateForm, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="accepted">Accept Task</SelectItem>
                  <SelectItem value="started">Start Work</SelectItem>
                  <SelectItem value="completed">Mark Complete</SelectItem>
                  <SelectItem value="escalated">Escalate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea 
                value={updateForm.notes} 
                onChange={e => setUpdateForm({...updateForm, notes: e.target.value})} 
                placeholder="Add notes about the work done..."
                rows={3}
              />
            </div>
            
            {/* Location Info */}
            {location && (
              <div className="p-3 bg-green-500/10 rounded-lg flex items-center gap-2">
                <MapPin className="w-4 h-4 text-green-500" />
                <span className="text-xs text-green-400">
                  Location will be recorded: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpdateDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateTask} disabled={submitting} data-testid="update-task-btn">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog - Push Notifications */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              Notification Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {isSupported ? (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Push Notifications</p>
                    <p className="text-sm text-muted-foreground">Receive alerts for new tasks</p>
                  </div>
                  <Switch 
                    checked={isSubscribed} 
                    onCheckedChange={(checked) => checked ? subscribe() : unsubscribe()}
                    disabled={pushLoading}
                    data-testid="push-toggle"
                  />
                </div>
                {isSubscribed && (
                  <Button variant="outline" className="w-full" onClick={sendTestNotification}>
                    Send Test Notification
                  </Button>
                )}
              </>
            ) : (
              <div className="text-center py-4">
                <BellOff className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">Push notifications are not supported on this browser</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSettingsDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t px-4 py-2 flex justify-around">
        <Button variant="ghost" className="flex-col h-14 px-4" onClick={() => setActiveTab("tasks")}>
          <List className="w-5 h-5" />
          <span className="text-xs mt-1">Tasks</span>
        </Button>
        <Button variant="ghost" className="flex-col h-14 px-4" onClick={() => setShowReportDialog(true)}>
          <Plus className="w-5 h-5" />
          <span className="text-xs mt-1">Report</span>
        </Button>
        <Button variant="ghost" className="flex-col h-14 px-4" onClick={() => navigate('/')}>
          <Home className="w-5 h-5" />
          <span className="text-xs mt-1">Home</span>
        </Button>
      </div>
    </div>
  );
}
